import { League } from '../../../_shared/models/league.model';
import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  KeyValueDiffers,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { Tie } from '../../../_shared/models/tie.model';

import { AuthService } from '../../../auth/auth.service';
import { TeamHelperService } from '../../../my-teams/team-helper.service';
import { TieDetailsService } from '../tie-details.service';
import { DownloadTieService } from './download-tie/download-tie.service';
import { Subject, takeUntil } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { ITA_URL } from '../../../../environments/environment';
import { ThemeService } from '../../../_core/services/theme.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  standalone: false,
  selector: 'app-tie-scorecard',
  templateUrl: './tie-scorecard.component.html',
  styleUrls: ['./tie-scorecard.component.scss'],
})
export class TieScorecardComponent implements OnInit, DoCheck {
  authService = inject(AuthService);
  private location = inject(Location);
  private activatedRoute = inject(ActivatedRoute);
  themeService = inject(ThemeService);
  private translateService = inject(TranslateService);

  @Input() tie: Tie;
  @Input() isListView = false;
  @Input() isDraw = false;
  @Input() isOwner = false;
  @Input() isOfficial = false;
  @Input() isTeamOwner = false;
  @Input() numberOfViewers = 0;
  @Input() isLeagueOwner: boolean;
  @Input() league: League;
  @Input() type = 'default';
  @Output() openMenu = new EventEmitter();
  isStartingTie: boolean;
  isTeamOwnerSide1: boolean;
  isTeamOwnerSide2: boolean;

  side1CheckmarkColor = 'light';
  side2CheckmarkColor = 'light';
  empDiffer: any;
  isTracker: boolean;

  private destroyed$: Subject<void> = new Subject();
  isStreamView = false;
  theme: string;

  constructor(
    private kvDiffers: KeyValueDiffers,
    public ths: TeamHelperService,
    private tieDetailsService: TieDetailsService,
    private downloadTieService: DownloadTieService,
    private router: Router
  ) {}

  async ngOnInit() {
    this.isStreamView = this.location.path().includes('stream-view');
    if (this.isStreamView) {
      this.theme = this.activatedRoute.snapshot.params.theme;
    }
    this.empDiffer = this.kvDiffers.find([]).create();
    this.getCheckmarkColor();
    this.isTracker = await this.tieDetailsService.isTracker(this.tie);

    await this.checkOwners();
    this.subscriptions();
  }

  ngDoCheck(): void {
    const empChanges = this.empDiffer.diff(this.tie);
    if (empChanges) {
      empChanges.forEachChangedItem((record) => {
        if (record.key === 'matchStatus') {
          this.getCheckmarkColor();
        }
      });
    }
  }

  public getInitials(sideNumber) {
    const teamName = this.getTeamName(sideNumber);
    const array = teamName?.split(' ');
    if (array?.length === 1) {
      return teamName?.charAt(0);
    } else if (array?.length === 2) {
      return array?.[0]?.charAt(0) + array?.[1]?.charAt(0);
    } else {
      return '';
    }
  }

  getTeamName(sideNumber): string|undefined {
    const team = this.getTeam(sideNumber);
    if (team) {
      return team.name;
    } else {
      return this.translateService.instant('No team');
    }
  }

  getAccronym(sideNumber): string|undefined {
    const team = this.getTeam(sideNumber);
    if (team?.abbreviation) {
      return team.abbreviation;
    } else {
      return this.translateService.instant('TBD');
    }
  }

  getTeam(sideNumber: number) {
    return this.tie?.sides?.[sideNumber - 1]?.team;
  }

  private getCheckmarkColor() {
    const winningSide = this.tie?.winningSide;
    if (winningSide === 1) {
      this.side1CheckmarkColor = 'dark';
      this.side2CheckmarkColor = 'light';
    } else if (winningSide === 2) {
      this.side2CheckmarkColor = 'dark';
      this.side1CheckmarkColor = 'light';
    } else {
      this.side1CheckmarkColor = 'light';
      this.side2CheckmarkColor = 'light';
    }
  }

  showInviteSelect(sideNumber) {
    // authUser is tie or side owner
    return (
      this.tie?.owners?.includes(this.authService.authUserSignal()?._id) ||
      this.tie?.sides?.[sideNumber - 1]?.owners?.includes(
        this.authService.authUserSignal()?._id
      )
    );
  }

  private subscriptions() {
    this.downloadTieService
      .subscribeDownloadedTie()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (downloadedTie: Tie) => {
        if (this.tie?.tieId === downloadedTie.tieId) {
          this.tie = downloadedTie;
          await this.checkOwners();
        }
      });
  }

  async checkOwners() {
    this.isTeamOwnerSide1 = await this.tieDetailsService.isTeamOwner(
      this.tie,
      0
    );
    this.isTeamOwnerSide2 = await this.tieDetailsService.isTeamOwner(
      this.tie,
      1
    );
  }

  navigateToTieDetails() {
    if (this.isListView || this.isDraw) {
      if (this.tie?._id) {
        this.router.navigate(['/ties', this.tie?._id]);
      } 
    }
  }

  navigateToTeam(sideNumber: number) {
    const teamId = this.getTeam(sideNumber)?._id;
    if (!this.isListView && teamId) {
      this.router.navigate([
        '/my-teams',
        teamId,
      ]);
    }
  }

  displayDownloadTie(): boolean {
    if (this.tie?.matchStatus === 'TBP') {
      if (!this.tie?._id && (this.isOwner || this.isOfficial)) return true;
      else if (this.isLeagueOwner && !this.tie.league) return true;
      else return false;
    } else {
      return false;
    }
  }

  drawLogoUrl(sideNumber): string | null {
    const teamId = this.getTeam(sideNumber)?.teamId;
    if (!this.tie?.organisations?.length || !teamId) {
      return null;
    }
    return `${ITA_URL}/${teamId.toUpperCase()}/Logo/`;
  }

  getScore(sideNumber: number): string {
    if (this.tie?.matchStatus !== 'TBP') {
      const segments = this.tie?.score?.split('-');
      const score = segments?.[sideNumber - 1];
      return score?.trim?.() ?? '0';
    }

    return '0';
  }
}
